// Studentren.service.ts
import { Injectable, inject, signal } from '@angular/core';
import { Student, School } from '@awo-care/shared-core';
import { sanitizeForFirestore } from '@awo-care/utils';
import { UserService } from '../../core/services/user.service';
import { SchoolService } from '@awo-care/schools';
import { DocumentReference, Firestore, collection, doc, getDoc, getDocs, orderBy, query, updateDoc, where } from 'firebase/firestore';
import { Auth } from 'firebase/auth';
import { FIREBASE_AUTH, FIREBASE_FIRESTORE } from '@awo-care/shared-firebase';

@Injectable({
  providedIn: 'root',
})
export class MyStudentService {
  firestore = inject(FIREBASE_FIRESTORE);
  userService = inject(UserService);
  schoolService = inject(SchoolService);
  private auth = inject(FIREBASE_AUTH);

  private studentCollection = collection(this.firestore, 'students');

  private studentSignal = signal<Student[]>([]);
  students = this.studentSignal.asReadonly();

  constructor() {
    // Automatically clear student cache when user logs out
    this.auth.onAuthStateChanged((user) => {
      if (!user) {
        this.studentSignal.set([]);
      }
    });
  }

  async getUserStudents(): Promise<Student[]> {
    try {
      const currentUser = this.userService.currentUser();
      if (!currentUser || !currentUser.uid) {
        console.warn('No valid user found for getUserStudents');
        return [];
      }

      const userId = currentUser.uid;
      const querySnapshot = await getDocs(
        query(
          this.studentCollection,
          where('userId', '==', userId),
          where('isActive', '==', true),
          orderBy('createdOn', 'desc')
        )
      );
      const Studentren = querySnapshot.docs.map(
        (doc) =>
          ({
            id: doc.id,
            ...doc.data(),
          } as Student)
      );
      this.studentSignal.set(Studentren);
      return Studentren;
    } catch (error) {
      console.error('Error getting Studentren:', error);
      throw error;
    }
  }

  async ownsStudent(studentId: string): Promise<boolean> {
    try {
      // Check if students are already loaded in the signal
      const currentStudents = this.students();

      if (currentStudents.length > 0) {
        // Students already loaded, just check the signal
        return currentStudents.some((student) => student.id === studentId);
      }

      // Students not loaded yet, load them first then check
      await this.getUserStudents();
      return this.students().some((student) => student.id === studentId);
    } catch (error) {
      console.error('Error checking student ownership:', error);
      return false;
    }
  }

  async getStudentById(id: string): Promise<Student | undefined> {
    try {
      const docRef = doc(this.studentCollection, id);
      const docSnapshot = await getDoc(docRef);
      if (docSnapshot.exists()) {
        return { id: docSnapshot.id, ...docSnapshot.data() } as Student;
      }
      return undefined;
    } catch (error) {
      console.error('Error getting Student:', error);
      throw error;
    }
  }

  async updateStudent(
    StudentId: string,
    data: Partial<Student>
  ): Promise<void> {
    try {
      const docRef = doc(this.studentCollection, StudentId);
      // Add metadata for change tracking (will be used by Cloud Function)
      const currentUser = this.userService.currentUser();
      const updateData = {
        ...sanitizeForFirestore(data),
        updatedOn: new Date(),
        lastModifiedBy: currentUser?.uid || 'unknown',
        lastModifiedByEmail: currentUser?.email || currentUser?.phoneNumber || 'unknown',
        lastModifiedSource: 'customer_ui',
      };

      // Ensure the data object is not empty
      await updateDoc(docRef, updateData);

      // Update the local signal
      const updatedStudentren = this.studentSignal().map((Student) =>
        Student.id === StudentId ? { ...Student, ...data } : Student
      );
      this.studentSignal.set(updatedStudentren);
    } catch (error) {
      console.error('Error updating Student:', error);
      throw error;
    }
  }

  // Get students by current status
  getStudentsByStatus(
    status: 'no_registration' | 'registered' | 'contracted'
  ): Student[] {
    return this.students().filter(
      (student) => student.currentStatus === status
    );
  }

  /**
   * Get school data from student data, resolving DocumentReference if needed
   */
  async getSchoolForStudent(student: Student): Promise<School | null> {
    // Try to resolve from schoolRef if available (most up-to-date)
    if (student.schoolRef) {
      try {
        const school = await this.schoolService.getSchool(student.schoolRef.id);
        if (school) {
          return school;
        }
      } catch (error) {
        console.error('Error resolving school reference:', error);
      }
    }

    // Try to resolve contract school if it's a DocumentReference
    const contractSchool = student.contract?.school;
    if (
      contractSchool &&
      typeof contractSchool === 'object' &&
      'path' in contractSchool
    ) {
      try {
        const school = await this.schoolService.getSchool(
          (contractSchool as DocumentReference).id
        );
        if (school) {
          return school;
        }
      } catch (error) {
        console.error('Error resolving contract school reference:', error);
      }
    }

    // Fallback to embedded data
    const registrationSchool = student.startInfo?.school;
    if (
      registrationSchool &&
      typeof registrationSchool === 'object' &&
      'schoolName' in registrationSchool
    ) {
      return registrationSchool as School;
    }

    if (
      contractSchool &&
      typeof contractSchool === 'object' &&
      'schoolName' in contractSchool
    ) {
      return contractSchool as School;
    }

    return null;
  }
}
